import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageSeo from "../components/PageSeo";
import { useLocale } from "../i18n/LocaleContext";
import { fetchBikeById, fetchBikes } from "../utils/bikesStorage";
import { SITE_URL } from "../seoConstants";

const PageWrapper = styled.div`
  margin: 0 auto;
  padding: 0;
`;

const PageTitle = styled.h1`
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  text-align: center;
  margin: 2rem 0 1rem;
  max-width: ${({ theme }) => theme.maxWidth.content};
  margin-left: auto;
  margin-right: auto;
  padding: 0 1.5rem;
  color: ${({ theme }) => theme.colors.text};
`;

const BikesGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth.content};
  margin: 0 auto 3rem;
  padding: 1.5rem;

  @media screen and (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
    padding: 1rem;
  }
`;

const Intro = styled.section`
  max-width: ${({ theme }) => theme.maxWidth.content};
  margin: 0 auto 1.5rem;
  padding: 0 1.5rem;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.0625rem;
  line-height: 1.65;

  p {
    margin: 0 0 0.85rem;
  }

  p:last-child {
    margin-bottom: 0;
  }

  a {
    color: ${({ theme }) => theme.colors.footerLink};
    font-weight: 500;
    text-decoration: none;
    text-underline-offset: 3px;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const BikeTile = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  text-align: left;
  transition: transform ${({ theme }) => theme.transition}, box-shadow ${({ theme }) => theme.transition},
    border-color ${({ theme }) => theme.transition};
  font-family: inherit;

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadow.lg};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (hover: none) {
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

const TileImage = styled.img`
  width: 100%;
  aspect-ratio: 4/3;
  background-color: ${({ theme }) => theme.colors.primarySoft};
  object-fit: cover;
  display: block;
`;

const TileContent = styled.div`
  padding: 1rem 1.1rem;
  width: 100%;
  min-width: 0;
`;

const TileName = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.15rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  overflow-wrap: anywhere;
`;

const TilePrice = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const TileSoldBadge = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: #b42318;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.25rem 0.65rem;
  border-radius: 4px;
  z-index: 1;
`;

const TileImageWrapper = styled.div`
  position: relative;
  width: 100%;

  ${({ $sold, theme }) =>
    $sold
      ? `&::after {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.35);
          pointer-events: none;
        }`
      : ""}
`;

const StatusMessage = styled.p`
  font-size: 1.0625rem;
  text-align: center;
  max-width: 600px;
  margin: 2rem auto 3rem;
  padding: 0 1.5rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textMuted};

  a {
    color: ${({ theme }) => theme.colors.footerLink};
    font-weight: 500;
  }
`;

const skeletonShimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

const SkeletonBone = styled.div`
  border-radius: ${({ theme }) => theme.radius.sm};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.bgMuted} 0%,
    ${({ theme }) => theme.colors.borderStrong} 45%,
    ${({ theme }) => theme.colors.bgMuted} 90%
  );
  background-size: 200% 100%;
  animation: ${skeletonShimmer} 1.35s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: ${({ theme }) => theme.colors.bgMuted};
  }
`;

const SkeletonTile = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadow.sm};
`;

const SkeletonImage = styled(SkeletonBone)`
  width: 100%;
  aspect-ratio: 4/3;
  border-radius: 0;
`;

const SkeletonContent = styled.div`
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SkeletonTitle = styled(SkeletonBone)`
  height: 1.2rem;
  width: 78%;
`;

const SkeletonPrice = styled(SkeletonBone)`
  height: 1.45rem;
  width: 32%;
`;

const LoadingHint = styled.p`
  text-align: center;
  margin: -1.5rem auto 2.5rem;
  padding: 0 1.5rem;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SKELETON_TILE_COUNT = 6;

function BikesLoadingSkeleton({ t }) {
  return (
    <>
      <BikesGrid aria-busy="true" aria-label={t("bikes.loadingLabel")}>
        {Array.from({ length: SKELETON_TILE_COUNT }, (_, index) => (
          <SkeletonTile key={index} aria-hidden="true">
            <SkeletonImage />
            <SkeletonContent>
              <SkeletonTitle />
              <SkeletonPrice />
            </SkeletonContent>
          </SkeletonTile>
        ))}
      </BikesGrid>
      <LoadingHint>{t("bikes.loadingHint")}</LoadingHint>
    </>
  );
}

/* ── Modal ── */

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow-y: auto;

  @media screen and (max-width: 860px) {
    padding: 0;
    align-items: flex-start;
  }
`;

const ModalCard = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 3fr 1.5fr;
  max-width: 1400px;
  width: 100%;
  max-height: 94vh;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.35);

  @media screen and (max-width: 860px) {
    grid-template-columns: 1fr;
    max-height: none;
    border-radius: 0;
    min-height: 100dvh;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 10;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 1.25rem;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.full};
  line-height: 1;
  transition: background ${({ theme }) => theme.transition}, color ${({ theme }) => theme.transition};
  box-shadow: ${({ theme }) => theme.shadow.sm};

  &:hover {
    background: ${({ theme }) => theme.colors.bgMuted};
    color: ${({ theme }) => theme.colors.text};
  }
`;

/* ── Image side ── */

const ImagePanel = styled.div`
  position: relative;
  background: #000;
  min-height: 0;
  display: flex;
  flex-direction: column;

  @media screen and (max-width: 860px) {
    aspect-ratio: 4 / 3;
  }
`;

const ImageArea = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  min-height: 0;
`;

const SlideshowImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ChevronButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $side }) => ($side === "left" ? "left: 0.75rem;" : "right: 0.75rem;")}
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  border: none;
  border-radius: ${({ theme }) => theme.radius.full};
  color: #fff;
  font-size: 1.6rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.25s ease, color 0.25s ease, transform 0.15s ease, box-shadow 0.25s ease;
  box-shadow: none;
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    color: #18181b;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    transform: translateY(-50%) scale(1.08);
  }

  &:active {
    transform: translateY(-50%) scale(0.96);
  }

  @media screen and (max-width: 480px) {
    width: 42px;
    height: 42px;
    font-size: 1.3rem;
  }
`;

const PaginationDots = styled.div`
  position: absolute;
  bottom: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-radius: ${({ theme }) => theme.radius.full};
  z-index: 2;
`;

const Dot = styled.button`
  width: ${({ $active }) => ($active ? "20px" : "8px")};
  height: 8px;
  border-radius: ${({ theme }) => theme.radius.full};
  border: none;
  padding: 0;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#fff" : "rgba(255, 255, 255, 0.4)")};
  transition: width 0.25s ease, background 0.2s ease;

  &:hover {
    background: ${({ $active }) => ($active ? "#fff" : "rgba(255, 255, 255, 0.7)")};
  }
`;

/* ── Info side ── */

const InfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem 2rem 2rem 1.75rem;
  overflow-y: auto;
  min-height: 0;

  @media screen and (max-width: 860px) {
    padding: 1.5rem;
  }
`;

const ModalTitle = styled.h2`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 0.35rem;
  line-height: 1.3;
`;

const ModalPrice = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.35rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
`;

const ModalSoldBanner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: #fef2f2;
  color: #b42318;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.35rem 0.85rem;
  border-radius: ${({ theme }) => theme.radius.full};
  margin-bottom: 1.25rem;
  border: 1px solid #fecaca;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: 0 0 1rem;
`;

const BikeDescription = styled.p`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.95rem;
  line-height: 1.65;
  margin: 0 0 1.25rem;
  white-space: pre-line;

  a {
    color: ${({ theme }) => theme.colors.footerLink};
    font-weight: 500;
    text-decoration: none;
    text-underline-offset: 3px;
    word-break: break-all;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SpecsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 1.25rem;
`;

const SpecRow = styled.div`
  display: flex;
  align-items: baseline;
  padding: 0.55rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const SpecLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.textMuted};
  min-width: 110px;
  flex-shrink: 0;
`;

const SpecValue = styled.span`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
`;

const ComponentsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const ComponentTag = styled.li`
  display: inline-block;
  background: ${({ theme }) => theme.colors.bgMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.25rem 0.6rem;
  border-radius: ${({ theme }) => theme.radius.full};
`;

const ContactButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  border: none;
  color: #fff;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.75rem 1.75rem;
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transition}, transform 0.15s ease;
  margin-top: auto;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const InfoSpacer = styled.div`
  flex: 1;
  min-height: 0.5rem;
`;

const CONTACT_EMAIL = "support@basementbikemechanic.com";

const URL_RE = /(https?:\/\/[^\s)<>]+)/g;

function linkifyText(text) {
  if (!text) return text;
  const parts = text.split(URL_RE);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    URL_RE.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    ) : (
      part
    )
  );
}

function getBikeIdFromHash(hash) {
  const value = (hash || "").replace(/^#/, "");
  const match = value.match(/^bike-(\d+)$/);
  if (!match) return null;
  return parseInt(match[1], 10);
}

function toAbsoluteUrl(src) {
  if (!src || typeof src !== "string") return null;
  if (src.startsWith("https://") || src.startsWith("http://")) return src;
  if (src.startsWith("data:")) return null;
  if (src.startsWith("/")) return `${SITE_URL}${src}`;
  return `${SITE_URL}/${src}`;
}

function BikesForSale() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, siteConfig: config, seo } = useLocale();
  const [bikes, setBikes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedBike, setSelectedBike] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const bikesPageUrl = `${SITE_URL}/bikes-for-sale`;
  const bikesListJsonLdId = `${bikesPageUrl}#itemlist`;

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError(false);

    fetchBikes(config.bikes)
      .then((loadedBikes) => {
        if (!isMounted) return;
        setBikes(loadedBikes);
      })
      .catch(() => {
        if (!isMounted) return;
        setBikes([]);
        setLoadError(true);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [config.bikes]);

  const loadBikeDetails = useCallback(async (bike) => {
    try {
      const fullBike = await fetchBikeById(bike.id);
      if (fullBike) {
        setSelectedBike(fullBike);
      }
    } catch (e) {
      console.warn("Failed to load full bike details", e);
    }
  }, []);

  const handleTileClick = (bike) => {
    navigate(
      { pathname: location.pathname, search: location.search, hash: `#bike-${bike.id}` },
      { replace: true }
    );
  };

  const handleClose = () => {
    setSelectedBike(null);
    navigate({ pathname: location.pathname, search: location.search, hash: "" }, { replace: true });
  };

  const nextSlide = () => {
    setSlideIndex((i) => (i + 1) % selectedBike.images.length);
  };

  const prevSlide = () => {
    setSlideIndex((i) => (i - 1 + selectedBike.images.length) % selectedBike.images.length);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  useEffect(() => {
    const id = getBikeIdFromHash(location.hash);
    if (id === null) {
      setSelectedBike(null);
      return;
    }
    const bike = bikes.find((b) => b.id === id);
    if (!bike) return;
    setSelectedBike(bike);
    setSlideIndex(0);
    loadBikeDetails(bike);
  }, [location.hash, bikes, loadBikeDetails]);

  return (
    <PageWrapper>
      <PageSeo
        title={seo.bikesTitle}
        description={seo.bikesDescription}
        path="/bikes-for-sale"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "@id": `${bikesPageUrl}#webpage`,
              url: bikesPageUrl,
              name: t("bikes.jsonLdName"),
              description: seo.bikesDescription,
              about: { "@id": `${SITE_URL}/#business` },
              mainEntity: { "@id": bikesListJsonLdId },
            },
            {
              "@type": "ItemList",
              "@id": bikesListJsonLdId,
              name: t("bikes.jsonLdListName"),
              url: bikesPageUrl,
              itemListOrder: "https://schema.org/ItemListOrderAscending",
              numberOfItems: bikes.length,
              itemListElement: bikes.map((bike, index) => {
                const images = (bike.images || []).map(toAbsoluteUrl).filter(Boolean);
                return {
                  "@type": "ListItem",
                  position: index + 1,
                  url: `${bikesPageUrl}#bike-${bike.id}`,
                  item: {
                    "@type": "Product",
                    "@id": `${bikesPageUrl}#product-${bike.id}`,
                    name: bike.name,
                    ...(images.length ? { image: images } : {}),
                    category: "Bicycle",
                    offers: {
                      "@type": "Offer",
                      url: `${bikesPageUrl}#bike-${bike.id}`,
                      priceCurrency: "USD",
                      price: bike.price,
                      availability: bike.status === "sold" ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
                      itemCondition: "https://schema.org/UsedCondition",
                      seller: { "@id": `${SITE_URL}/#business` },
                    },
                  },
                };
              }),
            },
          ],
        }}
      />
      <Header />
      <PageTitle>{t("bikes.pageTitle")}</PageTitle>
      <Intro>
        <p>
          {t("bikes.introP1Prefix")}{" "}
          <a href={`tel:${config.phone}`}>{config.phone}</a> {t("bikes.introP1Middle")}{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
        <p>{t("bikes.introP2")}</p>
      </Intro>
      {isLoading ? (
        <BikesLoadingSkeleton t={t} />
      ) : loadError || bikes.length === 0 ? (
        <StatusMessage>
          {loadError ? t("bikes.loadError") : t("bikes.noBikes")}{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          {loadError ? t("bikes.loadErrorSuffix") : t("bikes.inquireSuffix")}
        </StatusMessage>
      ) : (
        <BikesGrid>
          {bikes.map((bike) => (
            <BikeTile key={bike.id} id={`bike-${bike.id}`} onClick={() => handleTileClick(bike)} type="button">
              <TileImageWrapper $sold={bike.status === "sold"}>
                {bike.status === "sold" && <TileSoldBadge>{t("bikes.sold")}</TileSoldBadge>}
                <TileImage
                  src={bike.images[0]}
                  alt={t("bikes.tileAlt", { name: bike.name })}
                  loading="lazy"
                  decoding="async"
                />
              </TileImageWrapper>
              <TileContent>
                <TileName>{bike.name}</TileName>
                <TilePrice>{bike.status === "sold" ? t("bikes.sold") : `$${bike.price}`}</TilePrice>
              </TileContent>
            </BikeTile>
          ))}
        </BikesGrid>
      )}
      {selectedBike && (
        <ModalOverlay onClick={handleBackdropClick}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleClose} aria-label={t("bikes.close")}>
              ×
            </CloseButton>

            {/* ── Image panel ── */}
            <ImagePanel>
              <ImageArea>
                <SlideshowImage
                  src={selectedBike.images[slideIndex]}
                  alt={t("bikes.imageAlt", { name: selectedBike.name, index: slideIndex + 1 })}
                  loading="eager"
                />
                {selectedBike.images.length > 1 && (
                  <>
                    <ChevronButton $side="left" onClick={prevSlide} aria-label={t("bikes.prev")}>
                      ‹
                    </ChevronButton>
                    <ChevronButton $side="right" onClick={nextSlide} aria-label={t("bikes.next")}>
                      ›
                    </ChevronButton>
                    <PaginationDots>
                      {selectedBike.images.map((_, i) => (
                        <Dot
                          key={i}
                          $active={i === slideIndex}
                          onClick={() => setSlideIndex(i)}
                          aria-label={`Image ${i + 1}`}
                        />
                      ))}
                    </PaginationDots>
                  </>
                )}
              </ImageArea>
            </ImagePanel>

            {/* ── Info panel ── */}
            <InfoPanel>
              <ModalTitle>{selectedBike.name}</ModalTitle>
              {selectedBike.status === "sold" ? (
                <ModalSoldBanner>● {t("bikes.soldBanner")}</ModalSoldBanner>
              ) : (
                <ModalPrice>${selectedBike.price}</ModalPrice>
              )}
              {selectedBike.description && (
                <>
                  <Divider />
                  <BikeDescription>{linkifyText(selectedBike.description)}</BikeDescription>
                </>
              )}
              {(selectedBike.frame_size || selectedBike.wheel_size || (selectedBike.components && selectedBike.components.length > 0)) && (
                <>
                  <Divider />
                  <SpecsGrid>
                    {selectedBike.frame_size && (
                      <SpecRow>
                        <SpecLabel>{t("bikes.frameSize")}</SpecLabel>
                        <SpecValue>{selectedBike.frame_size}</SpecValue>
                      </SpecRow>
                    )}
                    {selectedBike.wheel_size && (
                      <SpecRow>
                        <SpecLabel>{t("bikes.wheelSize")}</SpecLabel>
                        <SpecValue>{selectedBike.wheel_size}</SpecValue>
                      </SpecRow>
                    )}
                    {selectedBike.components && selectedBike.components.length > 0 && (
                      <SpecRow>
                        <SpecLabel>{t("bikes.components")}</SpecLabel>
                        <SpecValue>
                          <ComponentsList>
                            {selectedBike.components.map((comp, i) => (
                              <ComponentTag key={i}>{comp}</ComponentTag>
                            ))}
                          </ComponentsList>
                        </SpecValue>
                      </SpecRow>
                    )}
                  </SpecsGrid>
                </>
              )}
              <InfoSpacer />
              {selectedBike.status !== "sold" && (
                <ContactButton href={`mailto:${CONTACT_EMAIL}?subject=Inquiry about ${encodeURIComponent(selectedBike.name)}`}>
                  ✉ {t("bikes.contactMe")}
                </ContactButton>
              )}
            </InfoPanel>
          </ModalCard>
        </ModalOverlay>
      )}
      <Footer onNavigatePrivacy={() => navigate("/privacy")} onNavigateTerms={() => navigate("/terms")} />
    </PageWrapper>
  );
}

export default BikesForSale;
